import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { buildFormStateFromFulfillmentPlan } from '@shipstation/client-common/ltl/freight-payload-mappers';
import type { FreightBookingFormState } from '@shipstation/client-common/components/freight-booking-wizard/freight-booking-wizard-types';
import type { LtlQuote } from '@shipstation/client-common/types/ltl';
import type { DimensionUnit } from '@shipstation/client-common/types/dimensions';
import type { BaseWeightUnit } from '@shipstation/client-common/types/weight';
import { getMajorUnitType } from '@shipstation/client-common/utils/weight-utils';

import Global from '../../store/global-selectors';

export type FreightPlanHydration = {
  formState: FreightBookingFormState;
  rates: LtlQuote[];
  selectedRate: LtlQuote | null;
};

export const useFreightBookingFormState = (fulfillmentPlanId: string | undefined): FreightPlanHydration | null => {
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit) as BaseWeightUnit;
  const displayWeightUnit = getMajorUnitType(weightUnit);
  const dimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) as DimensionUnit;

  const freightProviders = useSelector(Global.selectSellerFreightProviders);
  const connectedProviderId: number | undefined = freightProviders?.[0]?.sellerFreightProviderId ?? undefined;

  const fulfillmentPlan = useSelector((state: unknown) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));

  return useMemo(() => {
    if (!fulfillmentPlan) return null;
    return buildFormStateFromFulfillmentPlan(fulfillmentPlan, connectedProviderId, displayWeightUnit, dimensionUnit);
  }, [fulfillmentPlan, connectedProviderId, displayWeightUnit, dimensionUnit]);
};
